import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { EmpleadoRepository } from 'src/app/domain/service/empleado/empleado-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { MunicipioRepository } from 'src/app/domain/service/municipio/municipio-repository';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import { Persona } from 'src/app/domain/model/persona/persona';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { EmpleadoDetalle, EmpleadoRequest } from 'src/app/domain/model/empleado/empleado';
import { Perfil } from 'src/app/domain/model/catalogo/perfil';
import { Solucion } from 'src/app/domain/model/catalogo/solucion';
import { Proyecto } from 'src/app/domain/model/catalogo/proyecto';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import { TipoFacturacion } from 'src/app/domain/model/catalogo/tipo-facturacion';
import { GrupoConocimiento } from 'src/app/domain/model/catalogo/grupo-conocimiento';
import { HttpPerfilRepository } from 'src/app/infrastructure/adapters/catalogo/http-perfil.repository';
import { HttpSolucionRepository } from 'src/app/infrastructure/adapters/catalogo/http-solucion.repository';
import { HttpProyectoRepository } from 'src/app/infrastructure/adapters/catalogo/http-proyecto.repository';
import { HttpTipoAsesorRealRepository } from 'src/app/infrastructure/adapters/catalogo/http-tipo-asesor-real.repository';
import { HttpTipoFacturacionRepository } from 'src/app/infrastructure/adapters/catalogo/http-tipo-facturacion.repository';
import { HttpGrupoConocimientoRepository } from 'src/app/infrastructure/adapters/catalogo/http-grupo-conocimiento.repository';
import { TIPO_EMPLEADO, YES_VALUE, NO_VALUE } from 'src/app/common/constants-ejb';
import { formatAsMoneyLegacy } from 'src/app/common/input-utils';

/**
 * Legacy: `Constants.ASESOR_PREVENCION` ("AP") — único valor de Tipo de
 * Facturación para el que "Programable" (`rbgRiesgosProg`) se oculta
 * (`addChangeHandlerTipoAsesor()`, `ModificarEmpleadoViewImpl.java`). El resto
 * de valores (Integral, Riesgos, u otros) lo muestran.
 */
const TIPO_FACTURACION_PREVENCION = 'AP';

/**
 * Legacy: `changeTipoEmpleado()` (`ModificarEmpleadoViewImpl.java:750`) —
 * el Salario nunca se muestra para Directivo (`DR`), Directivo Técnico
 * (`DT`) ni Operaciones (`OP`), sin importar que el empleado tenga un
 * `ptsalarioBasico` cargado. Códigos literales de
 * `WidgetsNamesConstants.properties` (`empleadoDirectivo=DR`,
 * `empleadoDirTecnico=DT`, `empleadoOperaciones=OP`) — no se centralizan
 * en `ConstantsEJB.TipoEmpleado` todavía porque esa constante hoy solo
 * declara `ASESOR` (ver fase 28 del plan de "Modificar Empleado" para
 * ampliar el catálogo completo de tipos de empleado; esta regla es
 * independiente de esa ampliación y no depende de ella).
 */
const TIPOS_EMPLEADO_SIN_SALARIO = ['DR', 'DT', 'OP'];

/**
 * Opciones fijas de "Tipo de asignación" (`dstipoAsignacion`) — el legado
 * (`UtilidadesBuscadores.llenarListBoxTipoAsignacion()`) no las trae de un
 * catálogo de BD: son 4 textos fijos donde el código guardado es el mismo
 * texto visible (`WidgetsNamesConstants.properties`:
 * tipoAsignOpcAdnminProy/tipoAsignOpcMovilidad/tipoAsignOpcRiesgos/tipoAsignOpcNa).
 * "Administrativo/Proyectos" (24 caracteres) es el valor real más largo —
 * ver el fix de `EmpleadoRequest.dstipoAsignacion` (antes limitado a 10).
 */
const OPCIONES_TIPO_ASIGNACION: CatalogoOption[] = [
  { codigo: 'Administrativo/Proyectos', descripcion: 'Administrativo/Proyectos' },
  { codigo: 'Movilidad', descripcion: 'Movilidad' },
  { codigo: 'Riesgos', descripcion: 'Riesgos' },
  { codigo: 'N/A', descripcion: 'N/A' },
];

/** Legacy: `SubirFotoViewImpl` — `Constants.REGEX_IMAGE_FORMAT`. */
const REGEX_FORMATO_FOTO = /\.(jpg|jpeg|gif|png)$/i;

/** Legacy: `web.xml` — `maxSize` del uploader (30MB), ver EmpleadoWebMapper.MAX_FOTO_BYTES en el backend. */
const MAX_FOTO_BYTES = 30 * 1024 * 1024;

/** Legacy: `WidgetsNamesConstants.properties` — mensajes exactos de `SubirFotoViewImpl`. */
const MSG_FORMATO_FOTO_INVALIDO = 'Formato de imagen invalido, solo se permiten archivos con extensión jpg, gif, png y jpeg';
const MSG_FOTO_MUY_GRANDE = 'Imagen es demasiado grande';

/**
 * Facade del formulario "Modificar Empleado/Consultor" (HU1259115).
 *
 * <p>Legacy: `ModificarEmpleadoViewImpl` — formulario extenso (~2000 líneas)
 * con 3 secciones (Datos de Persona, Información Adicional de nómina
 * solo-lectura, Datos Consultor) y visibilidad condicional según si el
 * empleado es Asesor. Ver Administrar-Empleados-Asesores-seguimiento.md fase
 * 21 para el detalle del análisis exhaustivo del legado y su migración
 * completa (Grupo de Conocimiento, Tipo de Facturación, licencia S.O.,
 * Transporte/Rutas/Mixto/Pymes/Atención masiva/Bloqueo agenda, nómina
 * adicional de solo lectura).</p>
 */
@Injectable()
export class ModificarEmpleadoFacade implements OnDestroy {

  ngOnDestroy(): void {
    if (this.fotoObjectUrl) {
      URL.revokeObjectURL(this.fotoObjectUrl);
    }
  }

  private readonly empleadoRepository = inject(EmpleadoRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly municipioRepository = inject(MunicipioRepository);
  private readonly personaRepository = inject(PersonaRepository);
  private readonly perfilRepository = inject(HttpPerfilRepository);
  private readonly solucionRepository = inject(HttpSolucionRepository);
  private readonly proyectoRepository = inject(HttpProyectoRepository);
  private readonly tipoAsesorRealRepository = inject(HttpTipoAsesorRealRepository);
  private readonly tipoFacturacionRepository = inject(HttpTipoFacturacionRepository);
  private readonly grupoConocimientoRepository = inject(HttpGrupoConocimientoRepository);

  readonly opcionesTipoAsignacion = OPCIONES_TIPO_ASIGNACION;

  // ==================== Estado de carga ====================

  readonly cargando = signal(true);
  readonly guardando = signal(false);
  readonly errorCarga = signal<string | null>(null);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<'info' | 'warning' | 'error'>('info');

  // ==================== Datos precargados (solo lectura) ====================

  readonly detalle = signal<EmpleadoDetalle | null>(null);
  readonly municipioResidenciaDisplay = signal<string>('');
  readonly municipioPrestacionDisplay = signal<string>('');

  /**
   * Detalle completo de la Persona asociada, para "Ver Detalle" (legacy:
   * `activity.verDetallePersona()` → `ModificarPersonaActivity.mostrarDialogoPersonas(cdpersona)`).
   * A diferencia del legado (que ya tenía el `Tpsc_personasdto` completo
   * anidado en `empleadoDto`, sin fetch adicional), `EmpleadoDetalle` aquí
   * solo trae los campos de identificación/nombre — se necesita una
   * consulta aparte por `cdpersona` para los ~15 campos restantes
   * (dígito de verificación, tipo/tamaño de empresa, actividad económica,
   * sexo, fecha de nacimiento, email, municipio de nacimiento, etc.).
   */
  readonly personaDetalle = signal<Persona | null>(null);
  readonly cargandoPersonaDetalle = signal(false);
  readonly tiposEmpresaPersona = signal<CatalogoOption[]>([]);
  readonly tamaniosEmpresaPersona = signal<CatalogoOption[]>([]);
  private catalogosEmpresaPersonaCargados = false;

  // ==================== Foto (HU1259115) ====================
  // Legacy: panelFoto/tbNombreFoto/siSubirFoto (ModificarEmpleadoViewImpl) +
  // SubirFotoActivity/SubirFotoViewImpl (diálogo de selección). A diferencia
  // del legado (que sube el archivo a un servlet y lo deja en sesión HTTP
  // hasta que se graba el formulario), aquí la foto nueva viaja embebida en
  // base64 dentro del mismo PUT de Guardar (ver `form.fotoBase64`) — mismo
  // comportamiento observable: sin Guardar, la foto no cambia.

  /** URL para mostrar la foto actual (blob: object URL) — null si no tiene foto o aún no se ha cargado. */
  readonly fotoUrl = signal<string | null>(null);
  readonly cargandoFoto = signal(false);
  readonly errorFoto = signal<string | null>(null);
  private fotoObjectUrl: string | null = null;

  private async cargarFoto(detalle: EmpleadoDetalle): Promise<void> {
    if (!detalle.tieneFoto) {
      return;
    }
    this.cargandoFoto.set(true);
    try {
      const blob = await this.empleadoRepository.obtenerFoto(detalle.nmconsecutivo);
      this.setFotoUrl(blob);
    } catch (error) {
      console.error('Error cargando foto de empleado:', error);
    } finally {
      this.cargandoFoto.set(false);
    }
  }

  private setFotoUrl(blob: Blob | null): void {
    if (this.fotoObjectUrl) {
      URL.revokeObjectURL(this.fotoObjectUrl);
      this.fotoObjectUrl = null;
    }
    this.fotoObjectUrl = blob ? URL.createObjectURL(blob) : null;
    this.fotoUrl.set(this.fotoObjectUrl);
  }

  /**
   * Selecciona una foto nueva desde el diálogo "Subir Foto" (legacy:
   * `SubirFotoViewImpl.onClickAceptar()` → `verificarTamanioFoto()`).
   * Valida extensión y tamaño igual que el legado, con los mismos mensajes
   * exactos; si es válida, la codifica en base64 (queda en `form.fotoBase64`
   * para viajar en el próximo Guardar) y actualiza la previsualización.
   */
  async seleccionarFoto(file: File): Promise<void> {
    this.errorFoto.set(null);

    if (!REGEX_FORMATO_FOTO.test(file.name)) {
      this.errorFoto.set(MSG_FORMATO_FOTO_INVALIDO);
      return;
    }
    if (file.size > MAX_FOTO_BYTES) {
      this.errorFoto.set(MSG_FOTO_MUY_GRANDE);
      return;
    }

    const dataUrl = await this.leerComoDataUrl(file);
    this.form.fotoBase64 = dataUrl;
    this.setFotoUrl(file);
  }

  private leerComoDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  // ==================== Catálogos ====================

  readonly tiposEmpleado = signal<CatalogoOption[]>([]);
  readonly oficinas = signal<CatalogoOption[]>([]);
  readonly perfiles = signal<Perfil[]>([]);
  readonly tiposAsesorReal = signal<TipoAsesorReal[]>([]);
  readonly proyectos = signal<Proyecto[]>([]);
  readonly soluciones = signal<Solucion[]>([]);
  readonly tiposFacturacion = signal<TipoFacturacion[]>([]);
  readonly gruposConocimiento = signal<GrupoConocimiento[]>([]);

  // ==================== Formulario ====================

  form: EmpleadoRequest = this.formVacio();

  /**
   * Deliberadamente NO es un `computed()`: `computed()` solo se invalida
   * cuando lee una `Signal`, y `form` es un objeto plano mutado in-place
   * (requerido por el `[(ngModel)]` de los campos) — un `computed()` aquí se
   * evaluaría una única vez (con el valor inicial de `form`, antes de que
   * `cargar()` complete) y quedaría memoizado para siempre, sin reaccionar a
   * `mapDetalleAForm()` ni al cambio en vivo del dropdown de Tipo Empleado
   * (bug real encontrado al implementar esta fase: la sección "Datos
   * Consultor" nunca se habría podido cerrar/abrir en vivo). Un método plano
   * se re-evalúa en cada ciclo de detección de cambios, igual que cualquier
   * otra expresión de template.
   */
  esAsesor(): boolean {
    return this.form.cdtipoEmpleado === TIPO_EMPLEADO.ASESOR;
  }

  /** Legacy: `addChangeHandlerTipoAsesor()` — "Programable" se oculta solo para Tipo de Facturación = Prevención. Ver nota de `esAsesor()` sobre por qué no es `computed()`. */
  mostrarProgramable(): boolean {
    return this.form.cdtipoAsesor !== TIPO_FACTURACION_PREVENCION;
  }

  /**
   * Legacy: `changeTipoEmpleado()` — Salario formateado (`Utilidades.formatAsMoney()`)
   * y solo si `ptsalarioBasico > 0` y el tipo de empleado (el seleccionado
   * en vivo, no solo el cargado) no está en {@link TIPOS_EMPLEADO_SIN_SALARIO}.
   * El campo es de solo lectura (nómina); esto solo gobierna qué texto se
   * muestra, no la edición.
   */
  salarioDisplay(): string {
    const salario = this.detalle()?.ptsalarioBasico;
    if (salario == null || salario <= 0) {
      return '';
    }
    if (TIPOS_EMPLEADO_SIN_SALARIO.includes(this.form.cdtipoEmpleado)) {
      return '';
    }
    return formatAsMoneyLegacy(salario);
  }

  private formVacio(): EmpleadoRequest {
    return {
      cdpersona: 0,
      nmconsecOficina: 0,
      cdtipoEmpleado: '',
      dsusuarioRed: '',
    };
  }

  async cargar(nmconsecutivo: number): Promise<void> {
    this.cargando.set(true);
    this.errorCarga.set(null);
    try {
      const [detalle] = await Promise.all([
        this.empleadoRepository.obtenerDetalle(nmconsecutivo),
        this.cargarCatalogos(),
      ]);

      if (!detalle) {
        this.errorCarga.set('No se encontró el empleado solicitado.');
        return;
      }

      this.detalle.set(detalle);
      this.form = this.mapDetalleAForm(detalle);
      await this.cargarDisplaysMunicipio(detalle);
      await this.cargarFoto(detalle);
    } catch (error) {
      console.error('Error cargando empleado para modificar:', error);
      this.errorCarga.set('Ocurrió un error al cargar el empleado. Intente nuevamente.');
    } finally {
      this.cargando.set(false);
    }
  }

  private async cargarCatalogos(): Promise<void> {
    const [tiposEmpleado, oficinas, perfiles, tiposAsesorReal, proyectos, soluciones, tiposFacturacion, gruposConocimiento] = await Promise.all([
      this.catalogoRepository.getTiposEmpleado(),
      this.catalogoRepository.getOficinas(),
      this.perfilRepository.listarActivos(),
      this.tipoAsesorRealRepository.listarActivos(),
      this.proyectoRepository.listarActivos(),
      this.solucionRepository.listarActivos(),
      this.tipoFacturacionRepository.listarActivos(),
      this.grupoConocimientoRepository.listarActivos(),
    ]);
    this.tiposEmpleado.set(tiposEmpleado);
    this.oficinas.set(oficinas);
    this.perfiles.set(perfiles);
    this.tiposAsesorReal.set(tiposAsesorReal);
    this.proyectos.set(proyectos);
    this.soluciones.set(soluciones);
    this.tiposFacturacion.set(tiposFacturacion);
    this.gruposConocimiento.set(gruposConocimiento);
  }

  private async cargarDisplaysMunicipio(detalle: EmpleadoDetalle): Promise<void> {
    if (detalle.cdmunResidencia) {
      try {
        const municipio = await this.municipioRepository.getByCodigo(detalle.cdmunResidencia);
        this.municipioResidenciaDisplay.set(municipio?.dsmunicipio ?? detalle.cdmunResidencia);
      } catch {
        this.municipioResidenciaDisplay.set(detalle.cdmunResidencia);
      }
    }
    if (detalle.cdmunPrestacion) {
      try {
        const municipio = await this.municipioRepository.getByCodigo(detalle.cdmunPrestacion);
        this.municipioPrestacionDisplay.set(municipio?.dsmunicipio ?? detalle.cdmunPrestacion);
      } catch {
        this.municipioPrestacionDisplay.set(detalle.cdmunPrestacion);
      }
    }
  }

  seleccionarMunicipioResidencia(codigo: string, display: string): void {
    this.form.cdmunResidencia = codigo;
    this.municipioResidenciaDisplay.set(display);
  }

  limpiarMunicipioResidencia(): void {
    this.form.cdmunResidencia = undefined;
    this.municipioResidenciaDisplay.set('');
  }

  seleccionarMunicipioPrestacion(codigo: string, display: string): void {
    this.form.cdmunPrestacion = codigo;
    this.municipioPrestacionDisplay.set(display);
  }

  limpiarMunicipioPrestacion(): void {
    this.form.cdmunPrestacion = undefined;
    this.municipioPrestacionDisplay.set('');
  }

  /**
   * Legacy: `activity.verDetallePersona()` — abre un diálogo de solo
   * lectura con los datos completos de la Persona asociada al empleado.
   * `cdpersona` sale del detalle ya cargado (`this.detalle()`), no del
   * form (que no lo expone como campo editable).
   */
  async cargarDetallePersona(): Promise<void> {
    const cdpersona = this.detalle()?.cdpersona;
    if (!cdpersona) {
      return;
    }
    this.cargandoPersonaDetalle.set(true);
    try {
      // Los catálogos de tipo/tamaño de empresa solo se piden la primera
      // vez que se abre "Ver Detalle" en este formulario — un flag
      // explícito, no `.length`, porque un catálogo real puede resolver
      // vacío y seguiría re-pidiéndose en cada apertura si se usara eso
      // como condición.
      const [persona, tiposEmpresa, tamaniosEmpresa] = await Promise.all([
        this.personaRepository.getById(cdpersona),
        this.catalogosEmpresaPersonaCargados ? this.tiposEmpresaPersona() : this.catalogoRepository.getTiposEmpresa(),
        this.catalogosEmpresaPersonaCargados ? this.tamaniosEmpresaPersona() : this.catalogoRepository.getTamaniosEmpresa(),
      ]);
      this.personaDetalle.set(persona);
      this.tiposEmpresaPersona.set(tiposEmpresa);
      this.tamaniosEmpresaPersona.set(tamaniosEmpresa);
      this.catalogosEmpresaPersonaCargados = true;
    } catch (error) {
      console.error('Error cargando detalle de persona:', error);
      this.personaDetalle.set(null);
    } finally {
      this.cargandoPersonaDetalle.set(false);
    }
  }

  /**
   * Legacy: al cambiar Tipo Empleado a uno no-asesor, los campos exclusivos
   * de asesor se limpian (`changeTipoEmpleado()` llama `panelFiltros`-equivalente
   * al ocultar el bloque). Se replica limpiando el estado, no solo ocultando
   * el HTML, para no enviar datos obsoletos de un tipo de empleado anterior.
   *
   * <p>Legacy `getDTO()` (rama "else", no-asesor) no deja estos campos en
   * blanco/`null`: fija explícitamente 'N' en los 6 flags obligatorios
   * (`snadscrito`, `sndisponible_programacion`, `snespecializacion_higiene`,
   * `snriesgos_program`, `snatencion_masiva`, `snbloqueo_agenda`) y `""` en
   * los 4 campos de movilidad (`sncomputador`, `sntablet`, `snmodem`,
   * `dstipo_asignacion`) — no los deja indefinidos. Replicado aquí con los
   * mismos valores literales (antes se enviaba `undefined`, que Jackson
   * deserializa igual que `null` porque el backend sobrescribe la fila
   * completa — la diferencia real es `NULL` vs `'N'`/`''` en la columna
   * Oracle, que sí puede afectar reportes/consultas que filtren por el
   * valor exacto).</p>
   */
  onCambioTipoEmpleado(): void {
    if (this.esAsesor()) {
      this.sembrarDefaultsAsesor();
      return;
    }
    this.form.cdtipoAsesorReal = undefined;
    this.form.cdperfil = undefined;
    this.form.nmconsecutivoProyecto = undefined;
    this.form.nmconsecutivoSolucion = undefined;
    this.form.sndisponibleProgramacion = NO_VALUE;
    this.form.sncomputador = '';
    this.form.sntablet = '';
    this.form.snmodem = '';
    this.form.sncelular = undefined;
    this.form.dstipoAsignacion = '';
    this.form.nmcapacidad = undefined;
    this.form.sncalcProd = undefined;
    this.form.cdmunResidencia = undefined;
    this.form.cdmunPrestacion = undefined;
    this.form.snadscrito = NO_VALUE;
    this.form.ptvalorCobrarAdscrito = undefined;
    this.form.cdtipoAsesor = undefined;
    this.form.nmgrupoConocimiento = undefined;
    this.form.cdnumLicenciaSo = undefined;
    this.form.feresolucionLicenciaSo = undefined;
    this.form.dsentidadExpideLicSo = undefined;
    this.form.dstransporte = undefined;
    this.form.snruta = undefined;
    this.form.snmixto = undefined;
    this.form.snpymes = undefined;
    this.form.snespecializacionHigiene = NO_VALUE;
    this.form.snriesgosProgram = NO_VALUE;
    this.form.snatencionMasiva = NO_VALUE;
    this.form.snbloqueoAgenda = NO_VALUE;
    this.limpiarMunicipioResidencia();
    this.limpiarMunicipioPrestacion();
  }

  /**
   * Legacy: `inicializarCamposVisibles(true)` — defaults sembrados al abrir
   * el bloque de Datos Consultor por primera vez para este formulario
   * (Rutas='S', Mixto='S', Transporte='VARIABLE', el resto 'N'). Usa `??=`
   * para NO pisar valores ya cargados al editar un asesor existente — solo
   * siembra cuando el campo está vacío (p. ej. un empleado que pasa de
   * no-asesor a asesor por primera vez en este formulario).
   */
  private sembrarDefaultsAsesor(): void {
    this.form.snadscrito ??= NO_VALUE;
    this.form.sndisponibleProgramacion ??= NO_VALUE;
    this.form.snlider ??= NO_VALUE;
    this.form.snruta ??= YES_VALUE;
    this.form.snmixto ??= YES_VALUE;
    this.form.snpymes ??= NO_VALUE;
    this.form.snatencionMasiva ??= NO_VALUE;
    this.form.snbloqueoAgenda ??= NO_VALUE;
    this.form.dstransporte ??= 'VARIABLE';
    this.form.snespecializacionHigiene ??= NO_VALUE;
  }

  /** Legacy: al ocultar "Programable" (Tipo de Facturación = Prevención) se limpia su valor. */
  onCambioTipoFacturacion(): void {
    if (!this.mostrarProgramable()) {
      this.form.snriesgosProgram = undefined;
    }
  }

  /** Legacy: `addChangeHandlerNoAdscrito()` limpia el valor hora al desmarcar. */
  onCambioAdscrito(): void {
    if (this.form.snadscrito !== YES_VALUE) {
      this.form.ptvalorCobrarAdscrito = undefined;
    }
  }

  /**
   * Legacy: `addChangeHandlerSiEspecHigiene()` limpia (fuerza reingreso) los
   * 3 campos de licencia cada vez que se selecciona "Sí" — no al revés.
   * `addChangeHandlerNoEspecHigiene()` solo oculta el bloque, sin limpiar su
   * valor. Corrección respecto a una versión anterior de este método, que
   * limpiaba al marcar "No" (justo la condición contraria a la del legado).
   */
  onCambioEspecializacionHigiene(): void {
    if (this.form.snespecializacionHigiene === YES_VALUE) {
      this.form.cdnumLicenciaHigiene = undefined;
      this.form.feresolucionLicenciaHigiene = undefined;
      this.form.dsentidadExpideLicHigiene = undefined;
    }
  }

  private mapDetalleAForm(detalle: EmpleadoDetalle): EmpleadoRequest {
    return {
      cdpersona: detalle.cdpersona,
      nmconsecOficina: detalle.nmconsecOficina,
      cdtipoEmpleado: detalle.cdtipoEmpleado,
      dsusuarioRed: detalle.dsusuarioRed,
      dsemailCorporativo: detalle.dsemailCorporativo ?? undefined,
      dscargo: detalle.dscargo ?? undefined,
      dsprofesion: detalle.dsprofesion ?? undefined,
      cdperfil: detalle.cdperfil ?? undefined,
      cdcompania: detalle.cdcompania ?? undefined,
      sndisponibleProgramacion: detalle.sndisponibleProgramacion ?? undefined,
      snprogOutlook: detalle.snprogOutlook ?? undefined,
      snasigAutomatica: detalle.snasigAutomatica ?? undefined,
      snriesgosProgram: detalle.snriesgosProgram ?? undefined,
      snlider: detalle.snlider ?? undefined,
      feinicioContrato: detalle.feinicioContrato ?? undefined,
      fefinContrato: detalle.fefinContrato ?? undefined,
      snadscrito: detalle.snadscrito ?? undefined,
      ptvalorCobrarAdscrito: detalle.ptvalorCobrarAdscrito ?? undefined,
      snespecializacionHigiene: detalle.snespecializacionHigiene ?? undefined,
      cdnumLicenciaHigiene: detalle.cdnumLicenciaHigiene ?? undefined,
      feresolucionLicenciaHigiene: detalle.feresolucionLicenciaHigiene ?? undefined,
      dsentidadExpideLicHigiene: detalle.dsentidadExpideLicHigiene ?? undefined,
      ptsalarioBasico: detalle.ptsalarioBasico ?? undefined,
      dsnivelEstudio: detalle.dsnivelEstudio ?? undefined,
      cdtipoAsesorReal: detalle.cdtipoAsesorReal ?? undefined,
      cdtipoAsesor: detalle.cdtipoAsesor ?? undefined,
      nmconsecutivoProyecto: detalle.nmconsecutivoProyecto ?? undefined,
      sncomputador: detalle.sncomputador ?? undefined,
      sntablet: detalle.sntablet ?? undefined,
      snmodem: detalle.snmodem ?? undefined,
      sncelular: detalle.sncelular ?? undefined,
      dstipoAsignacion: detalle.dstipoAsignacion ?? undefined,
      nmcapacidad: detalle.nmcapacidad ?? undefined,
      sncalcProd: detalle.sncalcProd ?? undefined,
      cdmunResidencia: detalle.cdmunResidencia ?? undefined,
      cdmunPrestacion: detalle.cdmunPrestacion ?? undefined,
      nmconsecutivoSolucion: detalle.nmconsecutivoSolucion ?? undefined,
      nmgrupoConocimiento: detalle.nmgrupoConocimiento ?? undefined,
      cdnumLicenciaSo: detalle.cdnumLicenciaSo ?? undefined,
      feresolucionLicenciaSo: detalle.feresolucionLicenciaSo ?? undefined,
      dsentidadExpideLicSo: detalle.dsentidadExpideLicSo ?? undefined,
      dstransporte: detalle.dstransporte ?? undefined,
      snruta: detalle.snruta ?? undefined,
      snmixto: detalle.snmixto ?? undefined,
      snpymes: detalle.snpymes ?? undefined,
      snatencionMasiva: detalle.snatencionMasiva ?? undefined,
      snbloqueoAgenda: detalle.snbloqueoAgenda ?? undefined,
      snactivo: detalle.snactivo ?? undefined,
    };
  }

  /**
   * No setea un mensaje de éxito propio: este facade se destruye junto con
   * el modal al cerrarse (`providers: [ModificarEmpleadoFacade]` en el
   * container), así que un mensaje puesto aquí nunca llega a mostrarse. La
   * confirmación de guardado la muestra el listado
   * (`AdministrarEmpleadosComponent.onEmpleadoGuardado()`) después de
   * cerrar el diálogo. El mensaje de error sí se muestra aquí porque, en
   * ese caso, el modal permanece abierto.
   */
  async guardar(nmconsecutivo: number): Promise<boolean> {
    this.guardando.set(true);
    this.mensaje.set('');
    try {
      await this.empleadoRepository.actualizar(nmconsecutivo, this.form);
      return true;
    } catch (error: unknown) {
      console.error('Error modificando empleado:', error);
      this.mensaje.set(this.extraerMensajeError(error));
      this.mensajeTipo.set('error');
      return false;
    } finally {
      this.guardando.set(false);
    }
  }

  /**
   * Legacy: no aplica (mensaje genérico). Contrato real del backend:
   * `GlobalExceptionHandler.handleValidationException()` — `message` siempre
   * es el texto genérico "Error de validación en los datos de entrada";
   * `detail` trae el mensaje del primer campo inválido y `validationErrors`
   * la lista completa `{field, message}` (ver seguridad-girop §2.15). Se
   * concatenan todos los campos fallidos para que el usuario sepa
   * exactamente qué corregir, en vez de mostrar solo el texto genérico.
   */
  private extraerMensajeError(error: unknown): string {
    const FALLBACK = 'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';

    if (typeof error !== 'object' || error === null || !('error' in error)) {
      return FALLBACK;
    }

    let body: unknown = (error as { error?: unknown }).error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        body = null;
      }
    }
    if (!body || typeof body !== 'object') {
      return FALLBACK;
    }

    const { message, detail, validationErrors } = body as {
      message?: string;
      detail?: string;
      validationErrors?: Array<{ field?: string; message?: string }>;
    };

    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      const base = message || detail || FALLBACK;
      const detalles = validationErrors
        .map((v) => (v.field ? `${v.field}: ${v.message ?? ''}` : v.message ?? ''))
        .filter((s) => s.trim().length > 0)
        .join('\n');
      return detalles ? `${base}\n${detalles}` : base;
    }

    // Sin validationErrors[]: `detail` trae el detalle específico ("Por favor
    // ingrese correctamente los siguientes campos: ...") y `message` el texto
    // genérico ("Datos de entrada inválidos") — priorizar `detail`.
    return detail || message || FALLBACK;
  }
}
